import {
  ArrowDown,
  ArrowUp,
  Calendar,
  GripVertical,
  Pencil,
} from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import {
  type InfluencerCampaign,
  InfluencerCampaignTrafficLight,
  TRAFFIC_DOT,
} from "@/marketing/models/influencerCampaign";

import {
  formatDate,
  formatRange,
  initials,
  progressPct,
} from "@/marketing/lib/formatters";
import { useBrandLookup } from "@/marketing/components/brand/useBrandLookup";

const CARD_ACTION_CLASS =
  "tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-bg-accent hover:tw-text-foreground";

interface ReorderButtonsProps {
  onMoveUp?: (() => void) | undefined;
  onMoveDown?: (() => void) | undefined;
}

// Rendered on both the compact sorting row and the full card, so the labels and click guards
// cannot drift between the two.
function ReorderButtons({ onMoveUp, onMoveDown }: ReorderButtonsProps) {
  return (
    <>
      {onMoveUp && (
        <button
          type="button"
          aria-label="Move campaign up"
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={CARD_ACTION_CLASS}
        >
          <ArrowUp className="tw-h-3 tw-w-3" />
        </button>
      )}
      {onMoveDown && (
        <button
          type="button"
          aria-label="Move campaign down"
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className={CARD_ACTION_CLASS}
        >
          <ArrowDown className="tw-h-3 tw-w-3" />
        </button>
      )}
    </>
  );
}

function ProgressRow({
  label,
  current,
  total,
  pct,
  color,
}: {
  label: string;
  current: number;
  total: number | undefined;
  pct: number;
  color: string;
}) {
  return (
    <div>
      <div className="tw-mb-1 tw-flex tw-justify-between tw-text-[11px] tw-text-muted-foreground">
        <span>{label}</span>
        <span className="tw-font-medium">
          {current}/{total ?? 0}
        </span>
      </div>
      <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-muted">
        <div
          className={cn("tw-h-full tw-rounded-full", color)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface CampaignCardProps {
  campaign: InfluencerCampaign;
  onClick: () => void;
  onEdit?: () => void;
  onMoveUp?: (() => void) | undefined;
  onMoveDown?: (() => void) | undefined;
  onReorderDrop?: ((draggedCampaignId: string) => void) | undefined;
  isCompact?: boolean;
}

export function CampaignCard({
  campaign,
  onClick,
  onEdit,
  onMoveUp,
  onMoveDown,
  onReorderDrop,
  isCompact,
}: CampaignCardProps) {
  const lookupBrand = useBrandLookup();
  const brandName =
    lookupBrand(campaign.brandId)?.name ?? campaign.brandId.slice(0, 8);
  const onboarded = campaign.onboardedCount ?? 0;
  const posted = campaign.postedCount ?? 0;
  const onbPct = progressPct(onboarded, campaign.onboardingTarget);
  const postPct = progressPct(posted, campaign.postTarget);
  const isOverdue =
    campaign.postingDueDate &&
    new Date(campaign.postingDueDate).getTime() < Date.now();
  const clearDropIndicator = (element: HTMLElement) =>
    element.classList.remove("tw-ring-2", "tw-ring-primary/40", "tw-rounded-lg");

  const dropTargetProps = onReorderDrop
    ? {
        onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          e.currentTarget.classList.add(
            "tw-ring-2",
            "tw-ring-primary/40",
            "tw-rounded-lg",
          );
        },
        onDragLeave: (e: React.DragEvent<HTMLDivElement>) =>
          clearDropIndicator(e.currentTarget),
        // Deliberately left to bubble to the column: a card dragged in from another status is not in
        // this column's order, so the column's own handler still runs and applies the status change.
        onDrop: (e: React.DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          clearDropIndicator(e.currentTarget);
          const draggedCampaignId = e.dataTransfer.getData("text/campaignId");
          if (draggedCampaignId) onReorderDrop(draggedCampaignId);
        },
      }
    : {};

  const dragSourceProps = {
    draggable: true,
    onDragStart: (e: React.DragEvent<HTMLElement>) => {
      e.dataTransfer.setData("text/campaignId", campaign.campaignId);
      e.dataTransfer.effectAllowed = "move";
    },
  };

  // In sorting mode the column collapses to title rows, so many more drop targets fit on screen
  // than the ~1.6 full cards that otherwise do.
  if (isCompact) {
    return (
      <div className="tw-group tw-relative" {...dropTargetProps}>
        <button
          type="button"
          {...dragSourceProps}
          onClick={onClick}
          className={cn(
            "tw-flex tw-w-full tw-cursor-grab tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-bg-background tw-px-3 tw-py-2 tw-text-left tw-transition-shadow hover:tw-shadow-sm active:tw-cursor-grabbing",
            campaign.trafficLight === InfluencerCampaignTrafficLight.Red &&
              "tw-border-l-[3px] tw-border-l-red-500",
          )}
        >
          <GripVertical className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-muted-foreground" />
          {/* Name only: the brand pill crowded the row until neighbouring campaigns were
              indistinguishable, and picking a row to drag depends entirely on the name. */}
          <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium">
            {campaign.name}
          </span>
        </button>
        {/* Nudge controls live here too, so every reorder belongs to the same saved session. */}
        <div className="tw-absolute tw-right-2 tw-top-1/2 tw-z-10 tw-flex -tw-translate-y-1/2 tw-items-center tw-gap-1 tw-opacity-0 tw-transition-opacity focus-within:tw-opacity-100 group-hover:tw-opacity-100">
          <ReorderButtons onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
        </div>
      </div>
    );
  }

  return (
    <div className="tw-group tw-relative" {...dropTargetProps}>
      {(onMoveUp || onMoveDown || onEdit) && (
        <div className="tw-absolute tw-right-2 tw-top-2 tw-z-10 tw-flex tw-items-center tw-gap-1 tw-opacity-0 tw-transition-opacity focus-within:tw-opacity-100 group-hover:tw-opacity-100">
          <ReorderButtons onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
          {onEdit && (
            <button
              type="button"
              aria-label="Edit campaign"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={CARD_ACTION_CLASS}
            >
              <Pencil className="tw-h-3 tw-w-3" />
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        {...dragSourceProps}
        onClick={onClick}
        className={cn(
          "tw-w-full tw-cursor-grab tw-rounded-lg tw-border tw-bg-background tw-p-3 tw-text-left tw-transition-shadow hover:tw-shadow-sm active:tw-cursor-grabbing",
          campaign.trafficLight === InfluencerCampaignTrafficLight.Red &&
            "tw-border-l-[3px] tw-border-l-red-500",
        )}
      >
      <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
          <span className="tw-truncate tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-foreground/80">
            {brandName}
          </span>
          {campaign.trafficLight && (
            <span
              className={cn(
                "tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-full",
                TRAFFIC_DOT[campaign.trafficLight],
              )}
            />
          )}
        </div>
      </div>
      <div className="tw-mb-2 tw-line-clamp-2 tw-text-sm tw-font-semibold tw-leading-snug">
        {campaign.name}
      </div>
      <div className="tw-mb-3 tw-flex tw-items-center tw-gap-1.5 tw-text-[11px]">
        <span
          className={cn(
            "tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-px-1.5 tw-py-0.5",
            isOverdue
              ? "tw-border-red-200 tw-bg-red-50 tw-text-red-600"
              : "tw-border-border tw-bg-muted/50 tw-text-muted-foreground",
          )}
        >
          <Calendar className="tw-h-2.5 tw-w-2.5" />
          {formatDate(campaign.postingDueDate)}
        </span>
        <span className="tw-text-muted-foreground/70">
          {formatRange(campaign.startAt, campaign.endAt)}
        </span>
      </div>
      <div className="tw-mb-3 tw-flex tw-flex-col tw-gap-1.5">
        <ProgressRow
          label="Onboarding"
          current={onboarded}
          total={campaign.onboardingTarget}
          pct={onbPct}
          color="tw-bg-violet-600"
        />
        <ProgressRow
          label="Posts"
          current={posted}
          total={campaign.postTarget}
          pct={postPct}
          color="tw-bg-emerald-500"
        />
      </div>
      <div className="tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-violet-600 tw-text-[10px] tw-font-semibold tw-text-white">
          {initials(campaign.ownerId)}
        </div>
        <span className="tw-text-[11px] tw-text-muted-foreground">
          {campaign.productRefs?.length ?? 0} products
        </span>
      </div>
      </button>
    </div>
  );
}
